import { useState, useEffect } from "react";
import "./impersonationBanner.css";

/**
 * Persistent banner shown while viewing the platform as another user.
 *
 * The banner is the feature as much as the token is. Impersonation without a
 * constant, unmissable reminder is how someone emails a customer from the wrong
 * account, or reads a screen and reports it as their own. It sits above
 * everything, cannot be dismissed, and offers only one action: leave.
 *
 * State lives in localStorage rather than context because impersonation replaces
 * the auth token wholesale — the app reloads into the other user's session, and
 * anything held in memory is gone by the time this renders.
 */

export const IMP_KEY = "impersonation";

/** What we are currently impersonating, if anything. */
export function readImpersonation() {
  try {
    const raw = localStorage.getItem(IMP_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    // A stale entry left behind after the token expired would show a banner over
    // the operator's own session, which is worse than showing none.
    if (v && v.expiresAt && new Date(v.expiresAt) < new Date()) {
      localStorage.removeItem(IMP_KEY);
      return null;
    }
    return v;
  } catch (e) {
    return null;
  }
}

/** Swap back to the operator's own session. */
export function exitImpersonation() {
  try {
    const v = readImpersonation();
    if (v && v.previousToken) {
      localStorage.setItem("token", v.previousToken);
      if (v.previousRole) localStorage.setItem("userrole", v.previousRole);
      if (v.previousUser) localStorage.setItem("user", v.previousUser);
    } else {
      // No stored original: safer to end the session than to leave someone
      // holding a token that is not theirs.
      localStorage.removeItem("token");
    }
    localStorage.removeItem(IMP_KEY);
  } catch (e) { /* fall through to the reload */ }
  window.location.href = "/admin/admin-dashboard";
}

const ImpersonationBanner = () => {
  const [imp, setImp] = useState(readImpersonation());
  const [left, setLeft] = useState("");
  // Collapsed by default: a dot and a name is enough to remember by, and the
  // detail is one tap away when it is wanted.
  const [open, setOpen] = useState(false);

  // Deliberately floats rather than occupying layout. A full-width bar pushed the
  // page down by its own height, which on a phone is a meaningful slice of a
  // small screen - enough that the thing being checked no longer fits. A pill in
  // the corner stays unmissable without costing any vertical space.
  useEffect(() => {
    if (!imp) return undefined;
    const tick = () => {
      const v = readImpersonation();
      if (!v) { setImp(null); return; }
      if (v.expiresAt) {
        const mins = Math.max(0, Math.round((new Date(v.expiresAt) - new Date()) / 60000));
        setLeft(mins > 0 ? `${mins} min left` : "expired");
        if (mins <= 0) { exitImpersonation(); }
      }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [imp]);

  if (!imp) return null;

  const who = imp.name || imp.email || "another user";

  return (
    <div className={`imp-pill${open ? " open" : ""}`} role="alert">
      <button
        type="button"
        className="imp-pill-main"
        title={`Viewing as ${who}${imp.schoolName ? ` at ${imp.schoolName}` : ""} — read-only`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="imp-dot" aria-hidden="true" />
        <span className="imp-who">{who}</span>
      </button>

      {open && (
        <div className="imp-detail">
          <div>
            Viewing as <b>{who}</b>
            {imp.schoolName ? <> at <b>{imp.schoolName}</b></> : null}
          </div>
          <div className="imp-sub">read-only{left ? ` · ${left}` : ""}</div>
        </div>
      )}

      <button type="button" className="imp-exit" onClick={exitImpersonation}>
        Exit
      </button>
    </div>
  );
};

export default ImpersonationBanner;
